import React, { useState } from 'react';

type Props = {
  organizationName: string;
  open: boolean;
  loading?: boolean;
  onClose: () => void;
  onConfirm: (payload: {
    resetSearchUsage: boolean;
    resetNotificationUsage: boolean;
    resetJobUsage: boolean;
    reason: string;
  }) => Promise<void>;
};

export default function ResetOrganizationUsageModal({
  organizationName,
  open,
  loading = false,
  onClose,
  onConfirm,
}: Props) {
  const [confirmation, setConfirmation] = useState('');
  const [reason, setReason] = useState('');
  const [resetSearchUsage, setResetSearchUsage] = useState(true);
  const [resetNotificationUsage, setResetNotificationUsage] = useState(true);
  const [resetJobUsage, setResetJobUsage] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  if (!open) return null;

  const busy = loading || submitting;
  const canConfirm = confirmation.trim() === 'RESET' && reason.trim().length >= 3;

  async function handleConfirm() {
    if (!canConfirm) return;
    setSubmitting(true);
    setError('');
    try {
      await onConfirm({
        resetSearchUsage,
        resetNotificationUsage,
        resetJobUsage,
        reason: reason.trim(),
      });
      setConfirmation('');
      setReason('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Reset failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" onClick={busy ? undefined : onClose} role="presentation">
      <div
        className="modal card card-elevated"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
      >
        <h2>Reset usage for this organization?</h2>
        <p className="modal-lead">
          This will reset quota counters used for searches, notifications, and usage limits for{' '}
          <strong>{organizationName}</strong>. Existing searches, recipients, subscriptions, jobs, and audit
          history will not be deleted.
        </p>
        <div className="form-stack">
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={resetSearchUsage}
              onChange={(e) => setResetSearchUsage(e.target.checked)}
              disabled={busy}
            />
            Reset search usage counter
          </label>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={resetNotificationUsage}
              onChange={(e) => setResetNotificationUsage(e.target.checked)}
              disabled={busy}
            />
            Reset notification usage counter
          </label>
          <label className="checkbox-row">
            <input
              type="checkbox"
              checked={resetJobUsage}
              onChange={(e) => setResetJobUsage(e.target.checked)}
              disabled={busy}
            />
            Reset manual job usage counter
          </label>
          <label htmlFor="reset-usage-reason">Reason</label>
          <input
            id="reset-usage-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Testing quota behavior"
            disabled={busy}
          />
          <label htmlFor="reset-usage-confirm">Type RESET to confirm</label>
          <input
            id="reset-usage-confirm"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder="RESET"
            autoComplete="off"
            disabled={busy}
          />
        </div>
        {error ? <div className="alert alert-error">{error}</div> : null}
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-danger"
            onClick={() => void handleConfirm()}
            disabled={busy || !canConfirm}
          >
            {busy ? 'Resetting…' : 'Reset usage'}
          </button>
        </div>
      </div>
    </div>
  );
}
